import { useRef, useState } from "react";
import { Landmark } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useAutoSavePatch } from "../../lib/useAutoSavePatch";
import SaveStatus from "./SaveStatus";

/**
 * Vermittler bank / payout account form (IBAN, BIC, PayPal, account holder).
 *
 * Jun-2026 (User-Wunsch):
 *  - Auto-Save (kein „Speichern"-Button mehr) — Felder werden bei onBlur/Änderung gespeichert.
 *  - Kompakt: IBAN/BIC/Kontoinhaber nur bei „Bank-Überweisung", PayPal-E-Mail nur bei „PayPal".
 */
export default function BankForm({ user, onMethodChange }) {
    const [form, setForm] = useState({
        bank_iban: user.bank_iban || "",
        bank_bic: user.bank_bic || "",
        bank_account_holder: user.bank_account_holder || user.name || "",
        payout_method: user.payout_method || "bank",
        payout_paypal_email: user.payout_paypal_email || "",
    });
    const [error, setError] = useState({});
    const { save, saving, savedAt } = useAutoSavePatch("/users/me/vermittler-profile");
    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const snapshotRef = useRef({
        bank_iban: user.bank_iban || "",
        bank_bic: user.bank_bic || "",
        bank_account_holder: user.bank_account_holder || user.name || "",
        payout_method: user.payout_method || "bank",
        payout_paypal_email: user.payout_paypal_email || "",
    });

    // Speichert ein Feld nur bei echter Änderung und bietet „Rückgängig".
    const saveField = (key, newVal, label) => {
        const prev = snapshotRef.current[key];
        if ((prev ?? "") === (newVal ?? "")) return;
        snapshotRef.current[key] = newVal;
        save({ [key]: newVal || null }, {
            undo: {
                label: `${label} geändert`,
                patch: { [key]: (prev ?? "") || null },
                apply: () => { update(key, prev ?? ""); snapshotRef.current[key] = prev; },
            },
        });
    };

    const isBank = form.payout_method === "bank";
    const isPaypal = form.payout_method === "paypal";

    const changeMethod = (v) => {
        update("payout_method", v);
        setError({});
        onMethodChange?.(v);
        saveField("payout_method", v, "Auszahlungsart");
    };

    const saveIban = () => {
        const iban = (form.bank_iban || "").replace(/\s+/g, "").toUpperCase();
        if (iban && !/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)) {
            setError((e) => ({ ...e, bank_iban: "IBAN ungültig (z. B. DE89 3704 0044 0532 0130 00)" }));
            return;
        }
        setError((e) => ({ ...e, bank_iban: undefined }));
        update("bank_iban", iban);
        saveField("bank_iban", iban, "IBAN");
    };

    const saveBic = () => {
        const bic = (form.bank_bic || "").replace(/\s+/g, "").toUpperCase();
        if (bic && !/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic)) {
            setError((e) => ({ ...e, bank_bic: "BIC ungültig (8 oder 11 Zeichen)" }));
            return;
        }
        setError((e) => ({ ...e, bank_bic: undefined }));
        update("bank_bic", bic);
        saveField("bank_bic", bic, "BIC");
    };

    const saveHolder = () => saveField("bank_account_holder", form.bank_account_holder, "Kontoinhaber");

    const savePaypal = () => {
        if (!form.payout_paypal_email?.trim()) {
            setError((e) => ({ ...e, payout_paypal_email: "PayPal-E-Mail erforderlich" }));
            return;
        }
        setError((e) => ({ ...e, payout_paypal_email: undefined }));
        saveField("payout_paypal_email", form.payout_paypal_email.trim(), "PayPal-E-Mail");
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4 mt-4" data-testid="vermittler-bank-form">
            <div className="flex items-center gap-2 mb-1">
                <Landmark className="w-5 h-5 text-[#FFCC00]" />
                <h2 className="font-display font-bold text-base">Auszahlungs-Konto</h2>
                <SaveStatus saving={saving} savedAt={savedAt} className="ml-auto" />
            </div>
            <p className="text-xs text-gray-600 mb-3">
                Bevorzugte Auszahlungsart &amp; Bankverbindung. Diese Methode wird auch beim Einlösen verwendet.
            </p>

            <div className="max-w-sm">
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Bevorzugte Auszahlung</Label>
                <select
                    value={form.payout_method}
                    onChange={(e) => changeMethod(e.target.value)}
                    className="w-full h-10 rounded-sm border border-gray-200 mt-1 px-2 text-sm"
                    data-testid="v-payout-method"
                >
                    <option value="bank">Bank-Überweisung</option>
                    <option value="paypal">PayPal</option>
                    <option value="points_only">Nur Punkte sammeln (keine Auszahlung)</option>
                </select>
            </div>

            {/* PayPal — nur bei PayPal-Auswahl */}
            {isPaypal && (
                <div className="mt-4 max-w-sm" data-testid="v-paypal-wrap">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">PayPal-E-Mail</Label>
                    <Input
                        type="email"
                        value={form.payout_paypal_email}
                        onChange={(e) => update("payout_paypal_email", e.target.value)}
                        onBlur={savePaypal}
                        className="rounded-sm mt-1"
                        data-testid="v-paypal-email"
                    />
                    {error.payout_paypal_email && (
                        <div className="text-[11px] text-red-600 mt-1" data-testid="v-paypal-email-error">{error.payout_paypal_email}</div>
                    )}
                </div>
            )}

            {/* Bankverbindung — nur bei Bank-Überweisung */}
            {isBank && (
                <div className="mt-4 pt-4 border-t border-gray-100" data-testid="v-bank-wrap">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Bankverbindung</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="md:col-span-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">IBAN</Label>
                            <Input
                                value={form.bank_iban}
                                onChange={(e) => update("bank_iban", e.target.value.toUpperCase())}
                                onBlur={saveIban}
                                placeholder="DE89 3704 0044 0532 0130 00"
                                className="rounded-sm mt-1 font-mono-num"
                                data-testid="v-iban"
                            />
                            {error.bank_iban && (
                                <div className="text-[11px] text-red-600 mt-1" data-testid="v-iban-error">{error.bank_iban}</div>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">BIC (optional)</Label>
                            <Input
                                value={form.bank_bic}
                                onChange={(e) => update("bank_bic", e.target.value.toUpperCase())}
                                onBlur={saveBic}
                                placeholder="COBADEFFXXX"
                                className="rounded-sm mt-1 font-mono-num"
                                data-testid="v-bic"
                            />
                            {error.bank_bic && (
                                <div className="text-[11px] text-red-600 mt-1" data-testid="v-bic-error">{error.bank_bic}</div>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Kontoinhaber</Label>
                            <Input
                                value={form.bank_account_holder}
                                onChange={(e) => update("bank_account_holder", e.target.value)}
                                onBlur={saveHolder}
                                className="rounded-sm mt-1"
                                data-testid="v-account-holder"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
